import { createFileRoute, useSearch, Link, useNavigate } from "@tanstack/react-router";
import { dehydrate, hydrate, HydrationBoundary, useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  organisationEmailQueryOptions,
  organisationEmailsQueryOptions,
  deleteOrganisationEmailFn,
} from "@/server/emails";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { EmailType, EmailLanguage } from "@/lib/mma/types.gen";
import { EditEmailForm } from "../../../emails/-components/EditEmailForm";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PlusIcon } from "lucide-react";
import { ContextUser, hasWritePermission, hasDeletePermission } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/emails/")({
  component: OrganisationEmailComponent,
  validateSearch: (search: Record<string, unknown>) => {
    const validEmailTypes: EmailType[] = [
      "registrationVerificationEmail",
      "registrationConfirmEmail",
      "registrationAdminEmail",
      "resetPasswordEmail",
    ];

    const type =
      typeof search.type === "string" && validEmailTypes.includes(search.type as EmailType)
        ? (search.type as EmailType)
        : undefined;

    const language = typeof search.language === "string" ? search.language : undefined;

    return { type, language };
  },
  loaderDeps: ({ search }) => ({ type: search.type, language: search.language }),
  loader: async ({ params, context, deps }) => {
    const { type, language } = deps;

    if (type && language) {
      try {
        const emailQueryOptions = organisationEmailQueryOptions(Number(params.orgId), type, language);
        await context.queryClient.prefetchQuery(emailQueryOptions);
        const emailData = context.queryClient.getQueryData(emailQueryOptions.queryKey) as
          | { subject?: string }
          | undefined;
        return {
          crumb: emailData?.subject || `${type} (${language})`,
          dehydratedState: dehydrate(context.queryClient),
        };
      } catch (error) {
        return {
          crumb: `${type} (${language})`,
          dehydratedState: dehydrate(context.queryClient),
          user: context.user as ContextUser | null | undefined,
        };
      }
    }
    return {
      crumb: "Emails",
      dehydratedState: dehydrate(context.queryClient),
      user: context.user as ContextUser | null | undefined,
    };
  },
});

function OrganisationEmailComponent() {
  const { orgId } = Route.useParams();
  const search = useSearch({ from: "/_dashboard/organisations/$orgId/emails/" });
  const { dehydratedState, user } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState<{ type: EmailType; language: EmailLanguage } | null>(null);
  const [toastId, setToastId] = useState<string | number | null>(null);
  hydrate(queryClient, dehydratedState);
  const mayWrite = hasWritePermission(user, "EMAILS");
  const mayDelete = hasDeletePermission(user, "EMAILS");

  const hasSearchParams = !!(search.type && search.language);

  const emailsQuery = useQuery(organisationEmailsQueryOptions(Number(orgId)));
  const emails = emailsQuery.data || [];

  const emailQuery = useQuery(
    hasSearchParams
      ? organisationEmailQueryOptions(Number(orgId), search.type as EmailType, search.language as EmailLanguage)
      : {
          queryKey: ["email", "organisation", "disabled"],
          queryFn: async () => undefined,
          enabled: false,
        }
  );

  const deleteEmailMutation = useMutation({
    mutationFn: deleteOrganisationEmailFn,
    onMutate: () => {
      const id = toast.loading("Deleting email...");
      setToastId(id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organisationEmailsQueryOptions(Number(orgId)).queryKey });
      queryClient.removeQueries({ queryKey: ["email", "organisation"] });
      setShowDeleteDialog(null);
      if (toastId) {
        toast.dismiss(toastId);
      }
      toast.success("Email deleted successfully");
      setToastId(null);
    },
    onError: (error: any) => {
      if (toastId) {
        toast.dismiss(toastId);
      }
      toast.error(error?.message || "Failed to delete email");
      setToastId(null);
    },
  });

  const handleDeleteEmail = async () => {
    if (!showDeleteDialog) return;

    await deleteEmailMutation.mutateAsync({
      data: {
        orgId: Number(orgId),
        emailType: showDeleteDialog.type,
        language: showDeleteDialog.language,
      },
    });
  };

  return (
    <HydrationBoundary state={dehydratedState}>
      {!hasSearchParams && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Organisation Emails</h2>
            {mayWrite && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/organisations/$orgId/emails/new-email",
                    params: { orgId },
                    search: { type: undefined, language: undefined },
                  })
                }
                variant={"outline"}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add new email
              </Button>
            )}
          </div>
          {emailsQuery.isLoading ? (
            <Card>
              <CardContent>
                <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Loading...</p>
              </CardContent>
            </Card>
          ) : emails.length === 0 ? (
            <Card>
              <CardContent>
                <p className="text-md pb-20 pt-15 text-center text-muted-foreground">
                  No organisation email templates configured. Create one to get started.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {emails.map((email: any) => (
                <Card key={email.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base">{email.name}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {email.emailType} · {email.language}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to="/organisations/$orgId/emails"
                            params={{ orgId }}
                            search={{ type: email.emailType, language: email.language }}
                          >
                            Edit
                          </Link>
                        </Button>
                        {mayDelete && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setShowDeleteDialog({ type: email.emailType, language: email.language })}
                            disabled={deleteEmailMutation.isPending}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {hasSearchParams && emailQuery.isLoading && (
        <Card>
          <CardContent>
            <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Loading email...</p>
          </CardContent>
        </Card>
      )}

      {hasSearchParams && !emailQuery.isLoading && emailQuery.data && (
        <EditEmailForm
          scope="organisation"
          orgId={orgId}
          email={emailQuery.data}
          emailType={search.type}
          emailLanguage={search.language}
          user={user}
          returnTo={`/organisations/${orgId}/emails/`}
        />
      )}

      {hasSearchParams && !emailQuery.isLoading && !emailQuery.data && (
        <Card>
          <CardContent>
            <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Email template not found.</p>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={!!showDeleteDialog}
        title="Delete Email"
        description={`Are you sure you want to delete this email? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteEmail}
        onCancel={() => setShowDeleteDialog(null)}
        loading={deleteEmailMutation.isPending}
      />
    </HydrationBoundary>
  );
}
